import { KeyboardEvent } from 'react';
import PostCampaignHouseholdReferencesCsv from '../services/adapters/campaignHouseholdReferencesCsv';
import PostCampaignSummaryCsv from '../services/adapters/campaignSummaryCsv';
import { DisplayCampaignInformation } from '../types/campaigns.type';
import { FrontendFilterValue } from '../types/filters.type';
import type { OptionType } from '../types/forms.type';
import { KeyCode } from './constants';

type MinMaxValidationResult = {
  error: string | null;
  isValid: boolean;
  filters: FrontendFilterValue[];
};
const calculatePercent = (value: number, defaultMinimum: number, defaultMaximum: number) => {
  if (defaultMinimum > defaultMaximum) {
    throw new Error('defaultMinimum cannot be greater than defaultMaximum');
  }
  if (defaultMinimum === defaultMaximum) {
    throw new Error('defaultMinimum cannot be the same as defaultMaximum');
  }
  if (value < defaultMinimum || value > defaultMaximum) {
    throw new Error('value must be within the range of defaultMinimum and defaultMaximum');
  }
  return Math.round(((value - defaultMinimum) / (defaultMaximum - defaultMinimum)) * 100);
};
export default calculatePercent;

export const isValidInputKey = (event: KeyboardEvent<HTMLInputElement>) => {
  if ([
    KeyCode.Backspace,
    KeyCode.Delete,
    KeyCode.Tab,
    KeyCode.Escape,
    KeyCode.Enter,
    KeyCode.ArrowLeft,
    KeyCode.ArrowRight,
    KeyCode.Home,
    KeyCode.End
  ].includes(event.key as KeyCode)) {
    return true;
  }
  if (event.ctrlKey || event.metaKey) {
    return true;
  }
  return /^[0-9]$/.test(event.key);
};
export const validateMinAndMax = (minValue: string, maxValue: string, defaultMinimum: string, defaultMaximum: string): MinMaxValidationResult => {
  const defaultErrorMessage = `Please enter a valid number between ${defaultMinimum} and ${defaultMaximum} or leave both fields empty.`;
  const numericalMinimum = Number(minValue);
  const numericalMaximum = Number(maxValue);

  if (Number(defaultMinimum) > Number(defaultMaximum)) {
    throw new Error('defaultMinimum cannot be greater than defaultMaximum');
  }

  if (maxValue === '' && minValue === '') {
    return {
      error: null,
      isValid: true,
      filters: [],
    };
  }
  if (maxValue === '' || minValue === '') {
    return {
      error: defaultErrorMessage,
      isValid: false,
      filters: [],
    };
  }

  if (Number.isNaN(numericalMinimum) || Number.isNaN(numericalMaximum)) {
    return {
      error: defaultErrorMessage,
      isValid: false,
      filters: [],
    };
  }

  if (numericalMinimum < Number(defaultMinimum) || numericalMaximum > Number(defaultMaximum)) {
    return {
      error: defaultErrorMessage,
      isValid: false,
      filters: [],
    };
  }

  if (numericalMinimum > numericalMaximum) {
    return {
      error: defaultErrorMessage,
      isValid: false,
      filters: [],
    };
  }

  return {
    error: null,
    isValid: true,
    filters: [{ id: minValue, displayName: minValue }, { id: maxValue, displayName: maxValue }],
  };
};

export const getCampaignCsvUrl = (data: DisplayCampaignInformation): Promise<string> => {
  if (data.campaignType === 'pension_credit') {
    return new PostCampaignSummaryCsv().adaptResponse({ campaignCommunicationId: data.campaignCommunicationId! });
  }
  return new PostCampaignHouseholdReferencesCsv().adaptResponse({ campaignCommunicationId: data.campaignCommunicationId! });
};
const toYYMM = (raw: string) => raw.slice(2, 4) + raw.slice(0, 2);

export const createEndSnapshotOptions = (startSnapshot: string, allSnapshots: OptionType[]) => {
  const startRaw = allSnapshots.find((s) => s.label === startSnapshot)?.value
    ?? allSnapshots.find((s) => s.value === startSnapshot)?.value
    ?? '';
  const startYYMM = toYYMM(startRaw);

  const endSnapshotOptions = allSnapshots
    .filter((snapshot) => toYYMM(snapshot.value) > startYYMM)
    .sort((a, b) => toYYMM(b.value).localeCompare(toYYMM(a.value)))
    .map((snapshot) => ({ value: snapshot.value, label: snapshot.label }));
  return endSnapshotOptions;
};

export const reactElementIsType = <T>(e: unknown, type: T): type is T => typeof e === 'object' && !!e && 'type' in e && e.type === type;
